/** ProductsV2 — Redesign-Shell (S6/S7) mit Plan-012-Parität. ≤200 Zeilen. */
import { useCallback, useMemo, useEffect } from 'react'
import { useNavigate } from 'react-router-dom'
import { useQueryClient } from '@tanstack/react-query'
import { useReactTable, getCoreRowModel } from '@tanstack/react-table'
import { Package, RefreshCw } from 'lucide-react'
import { useTranslation } from 'react-i18next'
import { Loading, EmptyState, Pagination, Button } from '@/components/v2/ui'
import {
  ProductsHeader, ProductBulkActionBar,
  MergedProductTable, MergedTableToolbar, MergedProductsModals,
} from '@/components/v2/products'
import { ProductsOverviewHeader } from '@/components/v2/products/ProductsOverviewHeader'
import { ProductSyncStatusLine } from '@/components/v2/products/ProductSyncStatusLine'
import { ProductsReadinessLayer } from '@/components/v2/products/ProductsReadinessLayer'
import { ProductSegmentBar } from '@/components/v2/products/segments'
import { createShellColumns } from '@/components/v2/products/shellColumns'
import { ChannelRulesEntry } from '@/components/v2/products/channel-rules'
import { useMergedProductsActions } from '@/hooks/useMergedProductsActions'
import { useProductsPageData } from '@/hooks/useProductsPageData'
import { useProductsPageUi } from '@/hooks/useProductsPageUi'
import { useReadinessSummary, useRecompute } from '@/hooks/useChannelReadiness'
import { useAnalysisQuota } from '@/hooks/useImageIntelligence'
import { getProducts } from '@/lib/api/products'
import { filtersToQueryParams } from '@/lib/api/productFilterParams'
import type { ProductTableFilters } from '@/hooks/useProductTableConfig'
import type { ProductWithReadiness } from '@/types/products'
import type { ProductMeta } from '@/components/v2/channel-readiness/PackshotPickerDialog'

// Identische Formatierung wie SyncInfoCard.formatDate (de-DE, dd.mm.yyyy, hh:mm)
const formatSyncDate = (d: string | null | undefined): string | null =>
  d ? new Date(d).toLocaleString('de-DE', { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' }) : null

export function ProductsV2() {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const qc = useQueryClient()
  const ui = useProductsPageUi()
  const { products, total, totalPages, isLoading, isFetching, kpi, seriesByProduct, sync, config } =
    useProductsPageData(ui.channelId, ui.page, ui.search, ui.issueFilter, ui.imageStatusFilter)
  const actions = useMergedProductsActions(ui.selectedIds, products)
  const { data: summary = [] } = useReadinessSummary()
  const { data: quota } = useAnalysisQuota()
  const recompute = useRecompute()
  const lastSyncLabel = formatSyncDate(sync.statusData?.last_sync_at)
  const syncedCount = sync.statusData?.items_synced ?? null
  // "aktiv" = Kanal hat eine berechnete Readiness (avg_score > 0)
  const activeChannels = summary.filter(s => s.avg_score > 0).length

  useEffect(() => {
    if (sync.statusData?.status === 'completed') {
      qc.invalidateQueries({ queryKey: ['products'] })
      qc.invalidateQueries({ queryKey: ['products-count'] })
    }
  }, [sync.statusData?.status, sync.statusUpdatedAt, qc])

  const onPackshotAssign = useCallback((pid: string) => navigate(`/v2/products/${pid}?tab=assets`), [navigate])

  // S2-Segmentleiste: stabiler countFn (deps []) — SegmentLiveCount hat kein .catch()
  const countFn = useCallback(async (f: ProductTableFilters): Promise<number> => {
    try {
      const res = await getProducts({ page: 1, page_size: 1, ...filtersToQueryParams(f) })
      return res.total ?? 0
    } catch {
      return 0
    }
  }, [])

  const setDrawerProduct = ui.setDrawerProduct
  const columns = useMemo(
    () => createShellColumns(t, setDrawerProduct, onPackshotAssign, actions.setQuickViewProduct, actions.setGenerateProduct),
    [t, setDrawerProduct, onPackshotAssign, actions.setQuickViewProduct, actions.setGenerateProduct],
  )

  // eslint-disable-next-line react-hooks/incompatible-library -- TanStack Table API
  const table = useReactTable({
    data: products, columns,
    state: { sorting: config.sorting, columnVisibility: config.columnVisibility },
    onSortingChange: u => { config.setSorting(typeof u === 'function' ? u(config.sorting) : u); ui.setPage(1) },
    onColumnVisibilityChange: u => { config.setColumnVisibility(typeof u === 'function' ? u(config.columnVisibility) : u) },
    manualSorting: true, manualPagination: true, manualFiltering: true,
    getCoreRowModel: getCoreRowModel(),
    pageCount: totalPages,
    meta: { seriesByProduct },
  })

  const selectedIdsArray = useMemo(() => [...ui.selectedIds], [ui.selectedIds])
  const packshotProductMeta = useMemo<Record<string, ProductMeta>>(
    () => Object.fromEntries(products.map(p => [p.id, { name: p.name, sku: p.sku ?? '—', thumbnailUrl: p.first_image_url ?? undefined }])),
    [products],
  )

  return (
    <div className="merged-products-page p-4 sm:p-6 space-y-4">
      <ProductsHeader
        count={sync.count} syncStatus={sync.status} isSyncing={sync.isSyncing}
        isResetting={sync.isResetting} isSelecting={ui.selectedIds.size > 0}
        onStartSync={sync.startSync} onResetSync={sync.resetSync} onToggleSelect={ui.clearSelection}
      />
      <ProductsOverviewHeader kpi={kpi} onNavigate={navigate} />
      {/* S7: produktions-ehrliches Status-Band (vormals SyncInfoCard + gestrichelte Box) */}
      <ProductSyncStatusLine
        syncHealthy={sync.status !== 'error'}
        lastSyncLabel={lastSyncLabel}
        syncedCount={syncedCount}
        activeChannels={activeChannels}
        totalChannels={summary.length}
        channelRulesSlot={<ChannelRulesEntry />}
      />
      {ui.channelId && summary.length === 0 && (
        <div className="bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800 rounded-xl p-4 text-center">
          <p className="text-sm text-amber-800 dark:text-amber-200 mb-2">{t('products.topbar_no_scores')}</p>
          <Button size="sm" onClick={() => recompute.mutate({})} disabled={recompute.isPending}>
            <RefreshCw className={`h-4 w-4 mr-1.5 ${recompute.isPending ? 'animate-spin' : ''}`} />
            {recompute.isPending ? t('products.topbar_processing') : t('products.topbar_synchronize')}
          </Button>
        </div>
      )}

      <ProductsReadinessLayer
        summary={summary} selectedChannelId={ui.channelId} onSelectChannel={ui.handleSelectChannel}
        channelId={ui.channelId}
        issueFilter={ui.issueFilter} onSelectIssue={f => { ui.setIssueFilter(f); ui.setPage(1) }}
        imageStatusFilter={ui.imageStatusFilter} onImageStatusFilter={f => { ui.setImageStatusFilter(f); ui.setPage(1) }}
        quota={quota} drawerProduct={ui.drawerProduct} onCloseDrawer={() => ui.setDrawerProduct(null)}
        analyzeOpen={ui.analyzeOpen} onCloseAnalyze={ui.setAnalyzeOpen}
        packshotPickerOpen={ui.packshotPickerOpen} onClosePackshot={ui.setPackshotPickerOpen}
        selectedIdsArray={selectedIdsArray} packshotProductMeta={packshotProductMeta}
        onPackshotAnalyzeRequest={() => { ui.setPackshotPickerOpen(false); ui.setAnalyzeOpen(true) }}
        onAnalyze={() => ui.setAnalyzeOpen(true)}
      />

      <div className={ui.channelId ? 'grid grid-cols-1 lg:grid-cols-[280px_1fr] gap-4 items-start' : ''}>
        {ui.channelId && <div />}
        <div className="space-y-2 min-w-0">
          <div aria-live="polite" aria-atomic="true" className="sr-only">
            {isFetching && !isLoading ? 'Daten werden aktualisiert...' : ''}
          </div>
          {/* S2-Segmentleiste: Presets, Quick-Save, Segment-Chips (komponiert, ProductSegmentBar unmodifiziert) */}
          <ProductSegmentBar
            onApplyFilters={f => { config.setFilters(f); ui.setPage(1) }}
            currentFilters={config.filters}
            countFn={countFn}
          />
          <MergedTableToolbar
            table={table} search={ui.searchInput} onSearchChange={ui.setSearchInput}
            onSearchSubmit={ui.handleSearchSubmit}
            filters={config.filters}
            onFiltersChange={f => { config.setFilters(f); ui.setPage(1) }}
            onFiltersReset={() => { config.resetFilters(); ui.setPage(1) }}
            activeIssueFilter={ui.issueFilter}
            onClearIssueFilter={() => { ui.setIssueFilter(null); ui.setPage(1) }}
            onVisibilityChange={config.setColumnVisibility}
          />
          {isLoading ? (
            <div className="py-12"><Loading size="lg" label="Produkte werden geladen..." centered /></div>
          ) : products.length === 0 ? (
            <EmptyState icon={<Package className="h-16 w-16" />} title="Keine Produkte"
              description={ui.issueFilter ? 'Keine Produkte mit diesem Issue' : 'Noch keine Produkte vorhanden'} size="md" />
          ) : (
            <MergedProductTable
              table={table} selectedIds={ui.selectedIds} isFetching={isFetching}
              onToggleSelect={ui.onToggleSelect}
              onToggleSelectAll={() => {
                const all = products.every((p: ProductWithReadiness) => ui.selectedIds.has(p.id))
                ui.setSelectedIds(all ? new Set() : new Set(products.map((p: ProductWithReadiness) => p.id)))
              }}
              onRowClick={p => navigate(`/v2/products/${p.id}`)}
              onFileDrop={actions.handleFileDrop}
            />
          )}
          {totalPages > 1 && (
            <div className="flex items-center justify-between text-sm text-neutral-500 pt-2">
              <span>Seite {ui.page} von {totalPages} ({total} Produkte)</span>
              <Pagination currentPage={ui.page} totalPages={totalPages} onPageChange={ui.setPage} />
            </div>
          )}
        </div>
      </div>

      {ui.selectedIds.size > 0 && (
        <ProductBulkActionBar
          count={ui.selectedIds.size} onBatch={actions.navigateToBatch}
          onAnalyze={() => ui.setAnalyzeOpen(true)} onAssignAll={() => ui.setPackshotPickerOpen(true)}
          onHide={() => actions.bulkMutation.mutate({ ids: [...ui.selectedIds], active: false })}
          isBulkPending={actions.bulkMutation.isPending} onClear={ui.clearSelection}
        />
      )}
      <MergedProductsModals
        fileDropUpload={actions.fileDropUpload}
        quickViewProduct={actions.quickViewProduct} setQuickViewProduct={actions.setQuickViewProduct}
        generateProduct={actions.generateProduct} setGenerateProduct={actions.setGenerateProduct}
        onCloseSortPanel={actions.handleCloseSortPanel} onNavigate={path => navigate(path)}
      />
    </div>
  )
}

export default ProductsV2
